import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["data-target-uri"]
const _hoisted_2 = { class: "flex-grow min-w-0" }
const _hoisted_3 = { class: "text-xs text-muted truncate" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 0,
  class: "text-muted text-xs pl-3"
}

import { useDragAndDrop } from '@formkit/drag-and-drop/vue'
import type { DevRoeTalk } from '#shared/lex'

export interface TalkEntry {
  rkey: string
  uri: string
  cid: string
  value: DevRoeTalk.Record
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TalkDraggable',
  props: {
    talks: {},
    emptyHint: {},
    showPromote: { type: Boolean },
    forceTitleFromSource: { type: Boolean },
    topLevel: { type: Boolean },
    targetUri: {}
  },
  emits: ["dropped", "promote", "make-group"],
  setup(__props: any, { emit: __emit }) {

/**
 * Draggable list of talks. Sibling instances share a drag group so talks
 * can be moved between groups, or between top-level slots and groups,
 * via drag-and-drop. The parent page persists the change and re-fetches
 * the canonical date-sorted order; this component is a UI shell.
 */
const props = __props

const emit = __emit

const formatter = new Intl.DateTimeFormat('en', { dateStyle: 'medium' })

// Cross-list transfers only: talks are date-sorted server-side, so
// letting the user reorder within a list would lie about persistence.
// `dropZone: false` on top-level singletons rejects incoming drops
// while still allowing outgoing drags (`accepts: false` would block both).
const [parent, values] = useDragAndDrop<TalkEntry>([...props.talks], {
  group: 'admin-talks',
  dragHandle: '[data-drag-handle]',
  dropZone: !props.topLevel,
  performSort: () => {},
  onDragend: data => {
    const moved = data.draggedNode?.data.value as TalkEntry | undefined
    if (!moved) return
    const destEl = data.parent.el as HTMLElement
    const sourceEl = parent.value as HTMLElement | null
    if (destEl === sourceEl) return
    emit('dropped', moved, destEl?.dataset?.targetUri ?? '')
  },
})

watch(() => props.talks, next => {
  values.value = [...next]
}, { deep: false })

function rowTitle (talk: TalkEntry): string {
  if (props.forceTitleFromSource) return talk.value.source || talk.value.title || ''
  return talk.value.title || talk.value.source || ''
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtLink = _resolveComponent("NuxtLink")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("ul", {
      ref_key: "parent",
      ref: parent,
      class: "flex flex-col gap-2 min-h-12",
      "data-target-uri": __props.targetUri ?? ''
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(values), (talk) => {
        return (_openBlock(), _createElementBlock("li", {
          key: talk.rkey,
          class: "flex items-center gap-3 bg-accent px-3 py-2"
        }, [
          _cache[0] || (_cache[0] = _createElementVNode("span", {
            "data-drag-handle": "",
            class: "cursor-grab text-muted select-none"
          }, [
            _createElementVNode("span", { "aria-hidden": "true" }, "⋮⋮"),
            _createElementVNode("span", { class: "sr-only" }, "Drag to move between groups")
          ], -1)),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_NuxtLink, {
              to: `/admin/talks/${talk.rkey}`,
              class: "block underline-offset-4 hover:underline truncate"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(rowTitle(talk)), 1)
              ]),
              _: 2
            }, 1032, ["to"]),
            _createElementVNode("div", _hoisted_3, [
              _createTextVNode(_toDisplayString(_unref(formatter).format(new Date(talk.value.date))), 1),
              (!__props.forceTitleFromSource && talk.value.source)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" · " + _toDisplayString(talk.value.source), 1)
                  ], 64))
                : _createCommentVNode("", true),
              _createTextVNode(" · " + _toDisplayString(talk.value.type), 1)
            ])
          ]),
          (__props.showPromote)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "text-xs text-muted hover:text-primary transition-colors",
                onClick: ($event: any) => (emit('promote', talk))
              }, " Remove from group ", 8, _hoisted_4))
            : (talk.value.title)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "text-xs text-muted hover:text-primary transition-colors",
                  title: "Create a talk group seeded with this talk's title and description",
                  onClick: ($event: any) => (emit('make-group', talk))
                }, " Make group ", 8, _hoisted_5))
              : _createCommentVNode("", true)
        ]))
      }), 128))
    ], 8, _hoisted_1),
    (!_unref(values).length && __props.emptyHint)
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(__props.emptyHint), 1))
      : _createCommentVNode("", true)
  ], 64))
}
}

})